import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import Select from "react-select";
import Swal from "sweetalert2";
import enteFunciones from "../function/ente.Funcion";
import senaFunciones from "../function/sena.Funciones";

const MODULO = "Matriculas";
const MODULO_GRUPOS = "Grupos";
const MODULO_ENTES = "Entes Conformadores";

const RegistrarMatriculas = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEdit = useMemo(() => Boolean(id), [id]);

  const [anioLectivo, setAnioLectivo] = useState("");
  const [fichaGrupo, setFichaGrupo] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState("");

  const [coddaneEnteconformador, setCoddaneEnteconformador] = useState("");
  const [entes, setEntes] = useState([]);

  const [detallesMatricula, setDetallesMatricula] = useState([]);
  const [detallesEliminar, setDetallesEliminar] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);
  const [verificandoIdx, setVerificandoIdx] = useState(null);

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const normalizarTexto = (texto = "") =>
    String(texto)
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "");

  const obtenerModulosStorage = () => {
    try {
      const claves = ["permisos", "usuario", "user", "usuarios"];

      for (const clave of claves) {
        const item = localStorage.getItem(clave);
        if (!item) continue;

        const data = JSON.parse(item);

        if (Array.isArray(data)) return data;

        const modulos =
          data?.modulos ||
          data?.permisos ||
          data?.usuario?.modulos ||
          data?.usuario?.permisos ||
          data?.user?.modulos ||
          data?.user?.permisos ||
          [];

        if (Array.isArray(modulos)) return modulos;
      }

      return [];
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return [];
    }
  };

  const getModulo = (nombreModulo) => {
    const modulos = obtenerModulosStorage();
    const moduloBuscado = normalizarTexto(nombreModulo);

    return (
      modulos.find((modulo) => {
        const nombre =
          modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

        return normalizarTexto(nombre) === moduloBuscado;
      }) || {}
    );
  };

  const getModuloFlexible = (...nombres) => {
    for (const nombre of nombres) {
      const modulo = getModulo(nombre);

      if (modulo && Object.keys(modulo).length > 0) {
        return modulo;
      }
    }

    return {};
  };

  const tieneAccion = (modulo, accion) => {
    const accionLower = accion.toLowerCase();

    const campoPuede =
      accionLower === "crear"
        ? "puedeCrear"
        : accionLower === "leer"
          ? "puedeLeer"
          : accionLower === "actualizar"
            ? "puedeActualizar"
            : accionLower === "eliminar"
              ? "puedeEliminar"
              : "";

    return (
      modulo?.[campoPuede] === true ||
      modulo?.[accionLower] === true ||
      modulo?.[accion] === true ||
      modulo?.acciones?.[accion] === true ||
      modulo?.acciones?.[accionLower] === true
    );
  };

  const moduloMatriculas = getModuloFlexible(
    MODULO,
    "Matriculas",
    "Matrículas"
  );

  const moduloGrupos = getModuloFlexible(
    MODULO_GRUPOS,
    "Grupo",
    "Grupos",
    "Sena Grupos",
    "SenaGrupos"
  );

  const moduloEntes = getModuloFlexible(
    MODULO_ENTES,
    "EntesConformadores",
    "Entes Conformadores",
    "Ente Conformador",
    "EnteConformador",
    "Entes",
    "Ente"
  );

  const puedeCrear = tieneAccion(moduloMatriculas, "CREAR");
  const puedeActualizar = tieneAccion(moduloMatriculas, "ACTUALIZAR");
  const puedeEliminar = tieneAccion(moduloMatriculas, "ELIMINAR");

  const tienePermiso = isEdit ? puedeActualizar : puedeCrear;

  const disabledAll = loading || loadingCatalogos || !tienePermiso;

  const opcionesGrupos = useMemo(() => {
    return grupos
      .map((grupo) => {
        const ficha =
          grupo?.fichaGrupo ||
          grupo?.numeroFicha ||
          grupo?.numero_ficha ||
          grupo?.ficha ||
          grupo?.codigoGrupo ||
          grupo?.codigo ||
          grupo?._id ||
          "";

        const nombre =
          grupo?.nombreGrupo ||
          grupo?.nombre ||
          grupo?.descripcion ||
          grupo?.programa ||
          grupo?.programaFormacion ||
          "";

        return {
          value: String(ficha),
          label: nombre ? `${ficha} - ${nombre}` : String(ficha),
        };
      })
      .filter((item) => item.value);
  }, [grupos]);

  const opcionesEntes = useMemo(() => {
    return entes
      .map((ente) => {
        const codigo =
          ente?.coddaneEnteconformador ||
          ente?.codDaneEnteconformador ||
          ente?.codigoDane ||
          ente?.codDane ||
          ente?.codigo ||
          ente?._id ||
          "";

        const nombre =
          ente?.nombreEnteconformador ||
          ente?.nombreEnteConformador ||
          ente?.nombreEnte ||
          ente?.nombre ||
          ente?.razonSocial ||
          ente?.descripcion ||
          "";

        return {
          value: String(codigo),
          label: nombre ? `${codigo} - ${nombre}` : String(codigo),
        };
      })
      .filter((item) => item.value);
  }, [entes]);

  const cleanData = useCallback(() => {
    setAnioLectivo("");
    setFichaGrupo("");
    setGrupoSeleccionado("");
    setCoddaneEnteconformador("");
    setDetallesMatricula([]);
    setDetallesEliminar([]);
  }, []);

  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        setLoadingCatalogos(true);

        const gruposData = await senaFunciones.obtenerGrupos();
        const entesData = await enteFunciones.obtenerEntesConformadores();

        console.log("GRUPOS CARGADOS:", gruposData);
        console.log("ENTES CARGADOS:", entesData);

        setGrupos(Array.isArray(gruposData) ? gruposData : []);
        setEntes(Array.isArray(entesData) ? entesData : []);
      } catch (error) {
        console.error("Error cargando catálogos:", error);

        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error.response?.data?.message ||
            "No se pudieron cargar los grupos o entes conformadores.",
        });
      } finally {
        setLoadingCatalogos(false);
      }
    };

    cargarCatalogos();
  }, []);

  useEffect(() => {
    const fetchMatricula = async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          `/api/matriculas/listid/${id}`,
          authConfig()
        );

        const data = response.data;

        if (data.ok && data.matricula) {
          const matricula = data.matricula;

          setFichaGrupo(String(matricula.fichaGrupo || ""));
          setGrupoSeleccionado(String(matricula.fichaGrupo || ""));
          setAnioLectivo(String(matricula.anioLectivo || ""));
          setCoddaneEnteconformador(
            String(matricula.coddaneEnteconformador || "")
          );
        } else {
          Swal.fire({
            icon: "warning",
            title: "Atención",
            text: "No se encontró la matrícula.",
          });
        }
      } catch (error) {
        console.error("Error al cargar matrícula por ID:", error);

        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error.response?.data?.message ||
            "No se pudo cargar la matrícula con el ID proporcionado.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (isEdit && id && puedeActualizar) {
      fetchMatricula();
    }
  }, [id, isEdit, puedeActualizar]);

  useEffect(() => {
    const fetchDetalles = async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          `/api/detallematricula/listbyid/${id}`,
          authConfig()
        );

        const data = response.data;

        if (data.ok && data.detalles) {
          const detallesFormateados = data.detalles.map((detalle) => {
            const aprendiz = detalle.aprendizObjectId || {};

            const nombreCompleto = [
              aprendiz.nombre1Aprendiz,
              aprendiz.nombre2Aprendiz,
              aprendiz.apellido1Aprendiz,
              aprendiz.apellido2Aprendiz,
            ]
              .filter(Boolean)
              .join(" ")
              .trim()
              .replace(/\s+/g, " ");

            return {
              ...detalle,
              documentoAprendiz:
                detalle.documentoAprendiz ?? aprendiz.documentoAprendiz ?? "",
              nombreAprendiz: nombreCompleto,
              estadoMatricula: detalle.estadoMatricula || "",
              tipoMatricula: detalle.tipoMatricula || "",
            };
          });

          setDetallesMatricula(detallesFormateados);
          setDetallesEliminar([]);
        } else {
          setDetallesMatricula([]);
          setDetallesEliminar([]);
        }
      } catch (error) {
        console.error("Error al cargar detalles de matrícula:", error);

        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error.response?.data?.message ||
            "No se pudieron cargar los detalles de la matrícula.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (isEdit && id && puedeActualizar) {
      fetchDetalles();
    }
  }, [id, isEdit, puedeActualizar]);

  const handleChange = (index, field, value) => {
    setDetallesMatricula((prev) => {
      const nuevos = [...prev];
      nuevos[index] = { ...nuevos[index], [field]: value };
      return nuevos;
    });
  };

  const removeDetalle = async (index) => {
    if (!puedeEliminar) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para eliminar aprendices de la matrícula.",
        "warning"
      );
      return;
    }

    const detalle = detallesMatricula[index];

    if (!detalle) return;

    if (detallesMatricula.length === 1) {
      Swal.fire(
        "Acción no permitida",
        "La matrícula debe conservar al menos un aprendiz.",
        "warning"
      );
      return;
    }

    const resultado = await Swal.fire({
      icon: "warning",
      title: "¿Eliminar aprendiz?",
      text: detalle.nombreAprendiz
        ? `Se retirará a ${detalle.nombreAprendiz} de esta matrícula.`
        : "Se retirará este aprendiz de la matrícula.",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (!resultado.isConfirmed) return;

    // Si el detalle ya existe en MongoDB, guardar su _id para que el
    // backend elimine únicamente ese registro durante la actualización.
    if (isEdit && detalle._id) {
      setDetallesEliminar((prev) => {
        const detalleId = String(detalle._id);

        return prev.includes(detalleId) ? prev : [...prev, detalleId];
      });
    }

    // Si es un detalle nuevo, solamente se retira del estado local.
    setDetallesMatricula((prev) => prev.filter((_, i) => i !== index));
  };

  const addDetalle = () => {
    setDetallesMatricula((prev) => [
      ...prev,
      {
        documentoAprendiz: "",
        nombreAprendiz: "",
        estadoMatricula: "",
        tipoMatricula: "",
      },
    ]);
  };

  const verificarDocumentoAprendiz = async (documento, index) => {
    const doc = String(documento || "").trim();

    if (!doc) return;

    try {
      setVerificandoIdx(index);

      const response = await axios.get(
        `/api/aprendices/verificar/${doc}`,
        authConfig()
      );

      if (response.data?.exists) {
        handleChange(index, "nombreAprendiz", response.data.nombreCompleto || "");
      } else {
        handleChange(index, "nombreAprendiz", "");

        Swal.fire({
          icon: "warning",
          title: "Aprendiz no encontrado",
          text: "No se encontró un aprendiz con ese documento.",
        });
      }
    } catch (error) {
      console.error("Error al verificar el documento del aprendiz:", error);
      handleChange(index, "nombreAprendiz", "");
    } finally {
      setVerificandoIdx(null);
    }
  };

  const handleDocumentoAprendizChange = (e, index) => {
    handleChange(index, "documentoAprendiz", e.target.value);
    handleChange(index, "nombreAprendiz", "");
  };

  const handleDocumentoAprendizKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      verificarDocumentoAprendiz(e.target.value, index);
    }
  };

  const handleDocumentoAprendizBlur = (e, index) => {
    verificarDocumentoAprendiz(e.target.value, index);
  };

  const validarFormulario = () => {
    if (!fichaGrupo) {
      Swal.fire("Campo requerido", "Debe seleccionar un grupo.", "warning");
      return false;
    }

    if (!anioLectivo) {
      Swal.fire("Campo requerido", "Debe seleccionar el año lectivo.", "warning");
      return false;
    }

    if (!coddaneEnteconformador) {
      Swal.fire(
        "Campo requerido",
        "Debe seleccionar el ente conformador.",
        "warning"
      );
      return false;
    }

    if (detallesMatricula.length === 0) {
      Swal.fire(
        "Campo requerido",
        "Debe agregar al menos un aprendiz.",
        "warning"
      );
      return false;
    }

    const detalleIncompleto = detallesMatricula.some(
      (d) =>
        !String(d.documentoAprendiz || "").trim() ||
        !String(d.estadoMatricula || "").trim() ||
        !String(d.tipoMatricula || "").trim()
    );

    if (detalleIncompleto) {
      Swal.fire(
        "Información incompleta",
        "Todos los aprendices deben tener documento, estado y tipo de matrícula.",
        "warning"
      );
      return false;
    }

    return true;
  };

  const generarPayload = () => ({
    fichaGrupo: Number(fichaGrupo),
    anioLectivo: Number(anioLectivo),
    coddaneEnteconformador: Number(coddaneEnteconformador),

    detallesMatricula: detallesMatricula.map((detalle) => {
      const detallePayload = {
        documentoAprendiz: Number(detalle.documentoAprendiz),
        estadoMatricula: detalle.estadoMatricula,
        tipoMatricula: detalle.tipoMatricula,
        fichaGrupo: Number(fichaGrupo),
      };

      // Los detalles existentes deben conservar su _id para que el
      // backend los actualice sin borrarlos ni volverlos a crear.
      if (detalle._id) {
        detallePayload._id = String(detalle._id);
      }

      return detallePayload;
    }),

    // Solo el update utiliza este arreglo. En el registro será vacío.
    detallesEliminar: isEdit ? detallesEliminar : [],
  });

  const saveMatricula = async () => {
    const payload = generarPayload();

    const response = await axios.post(
      "/api/matriculas/add",
      payload,
      authConfig()
    );

    await Swal.fire({
      icon: "success",
      title: "Matrícula registrada con éxito",
      text: response.data?.message || "",
      showConfirmButton: false,
      timer: 2000,
    });

    cleanData();
    navigate("/matricula/listarmatriculas");
  };

  const updateMatricula = async () => {
    const payload = generarPayload();

    const response = await axios.put(
      `/api/matriculas/update/${id}`,
      payload,
      authConfig()
    );

    await Swal.fire({
      icon: "success",
      title: "Matrícula actualizada con éxito",
      text: response.data?.message || "",
      showConfirmButton: false,
      timer: 2000,
    });

    cleanData();
    navigate("/matricula/listarmatriculas");
  };

  const actions = async (e) => {
    e.preventDefault();

    if (!tienePermiso) {
      Swal.fire(
        "Sin permisos",
        isEdit
          ? "No tiene permisos para actualizar matrículas."
          : "No tiene permisos para crear matrículas.",
        "warning"
      );
      return;
    }

    if (!validarFormulario()) return;

    try {
      setLoading(true);

      if (isEdit) {
        await updateMatricula();
      } else {
        await saveMatricula();
      }
    } catch (error) {
      console.error("Error al guardar la matrícula:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "No se pudo guardar la matrícula.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGruposChange = (opcion) => {
    const value = opcion?.value || "";
    setGrupoSeleccionado(value);
    setFichaGrupo(value);
  };

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const start = 2015;
    return Array.from({ length: current - start + 1 }, (_, i) => start + i);
  }, []);

  if (!tienePermiso) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          {isEdit
            ? "No tiene permisos para actualizar matrículas."
            : "No tiene permisos para crear matrículas."}
        </div>

        <button
          className="btn btn-dark"
          type="button"
          onClick={() => navigate("/matricula/listarmatriculas")}
        >
          Volver
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit ? "Editar Matrícula" : "Registrar Matrícula"}
                </h5>

                <small className="text-secondary">
                  Selecciona año, grupo y ente. Luego gestiona los aprendices
                  matriculados.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                {!isEdit && (
                  <button
                    type="button"
                    className="btn btn-outline-dark"
                    onClick={cleanData}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-eraser me-2"></i>
                    Limpiar
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {(loading || loadingCatalogos) && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form
              id="form-matricula"
              onSubmit={actions}
              className="needs-validation"
              noValidate
            >
              <div className="border rounded-4 p-3 p-md-4 mb-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                  <div className="fw-semibold">Datos generales</div>

                  <span className="badge text-bg-light border">
                    {detallesMatricula.length} aprendiz
                    {detallesMatricula.length === 1 ? "" : "es"}
                  </span>
                </div>

                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold">
                      Año Lectivo
                    </label>

                    <select
                      className="form-select"
                      value={anioLectivo}
                      onChange={(e) => setAnioLectivo(e.target.value)}
                      required
                      disabled={disabledAll}
                    >
                      <option value="">Seleccione…</option>

                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold">
                      Grupo / Ficha
                    </label>

                    <Select
                      options={opcionesGrupos}
                      value={
                        grupoSeleccionado
                          ? opcionesGrupos.find(
                            (op) => op.value === grupoSeleccionado
                          ) || null
                          : null
                      }
                      onChange={handleGruposChange}
                      placeholder="Seleccione un grupo…"
                      isSearchable
                      isClearable
                      className="form-control p-0"
                      classNamePrefix="react-select"
                      isDisabled={disabledAll}
                      noOptionsMessage={() =>
                        loadingCatalogos
                          ? "Cargando grupos..."
                          : "No hay grupos disponibles"
                      }
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">
                      Ente Conformador
                    </label>

                    <Select
                      options={opcionesEntes}
                      value={
                        coddaneEnteconformador
                          ? opcionesEntes.find(
                            (op) =>
                              op.value === String(coddaneEnteconformador)
                          ) || null
                          : null
                      }
                      onChange={(opcion) =>
                        setCoddaneEnteconformador(opcion?.value || "")
                      }
                      placeholder="Seleccione un ente…"
                      isSearchable
                      isClearable
                      className="form-control p-0"
                      classNamePrefix="react-select"
                      isDisabled={disabledAll}
                      noOptionsMessage={() =>
                        loadingCatalogos
                          ? "Cargando entes..."
                          : "No hay entes disponibles"
                      }
                    />
                  </div>
                </div>

                <div className="small text-secondary mt-3">
                  Grupos cargados: {grupos.length} | Entes cargados:{" "}
                  {entes.length}
                </div>
              </div>

              <div className="border rounded-4 p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div>
                    <div className="fw-semibold">Gestión de Matriculados</div>

                    <div className="small text-secondary">
                      Ingresa el documento y valida automáticamente el nombre
                      del aprendiz.
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={addDetalle}
                    disabled={disabledAll}
                  >
                    <i className="fa-solid fa-user-plus me-2"></i>
                    Agregar
                  </button>
                </div>

                <div className="row text-secondary small fw-semibold d-none d-md-flex mb-2">
                  <div className="col-md-3">Documento</div>
                  <div className="col-md-4">Nombre</div>
                  <div className="col-md-2">Estado</div>
                  <div className="col-md-2">Tipo</div>
                  <div className="col-md-1 text-end"></div>
                </div>

                {detallesMatricula.length === 0 ? (
                  <div className="text-center text-secondary py-4 border rounded-4">
                    <div className="display-6 mb-2">👥</div>

                    <div className="fw-semibold">Aún no hay aprendices</div>

                    <div className="small mb-3">
                      Haz clic en “Agregar” para comenzar.
                    </div>

                    <button
                      type="button"
                      className="btn btn-dark"
                      onClick={addDetalle}
                      disabled={disabledAll}
                    >
                      <i className="fa-solid fa-user-plus me-2"></i>
                      Agregar Aprendiz
                    </button>
                  </div>
                ) : (
                  detallesMatricula.map((detalle, index) => (
                    <div
                      key={detalle._id || `nuevo-${index}`}
                      className="row align-items-end gy-2 mb-3"
                    >
                      <div className="col-12 col-md-3">
                        <label className="form-label d-md-none fw-semibold">
                          Documento
                        </label>

                        <div className="input-group">
                          <span className="input-group-text">
                            <i className="fa-regular fa-id-card"></i>
                          </span>

                          <input
                            type="number"
                            className="form-control"
                            placeholder="Documento"
                            value={detalle.documentoAprendiz || ""}
                            onChange={(e) =>
                              handleDocumentoAprendizChange(e, index)
                            }
                            onKeyDown={(e) =>
                              handleDocumentoAprendizKeyDown(e, index)
                            }
                            onBlur={(e) =>
                              handleDocumentoAprendizBlur(e, index)
                            }
                            required
                            disabled={disabledAll}
                          />

                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() =>
                              verificarDocumentoAprendiz(
                                detalle.documentoAprendiz,
                                index
                              )
                            }
                            disabled={
                              disabledAll ||
                              !String(detalle.documentoAprendiz || "").trim()
                            }
                          >
                            <i
                              className={`fa-solid ${verificandoIdx === index
                                  ? "fa-spinner fa-spin"
                                  : "fa-magnifying-glass"
                                }`}
                            ></i>
                          </button>
                        </div>
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label d-md-none fw-semibold">
                          Nombre
                        </label>

                        <input
                          type="text"
                          className="form-control"
                          placeholder="Se completa automáticamente"
                          value={detalle.nombreAprendiz || ""}
                          readOnly
                          disabled
                        />
                      </div>

                      <div className="col-12 col-md-2">
                        <label className="form-label d-md-none fw-semibold">
                          Estado
                        </label>

                        <select
                          className="form-select"
                          value={detalle.estadoMatricula || ""}
                          onChange={(e) =>
                            handleChange(
                              index,
                              "estadoMatricula",
                              e.target.value
                            )
                          }
                          required
                          disabled={disabledAll}
                        >
                          <option value="">Seleccione…</option>
                          <option value="Matriculado">Matriculado</option>
                          <option value="Pendiente">Pendiente</option>
                        </select>
                      </div>

                      <div className="col-12 col-md-2">
                        <label className="form-label d-md-none fw-semibold">
                          Tipo
                        </label>

                        <select
                          className="form-select"
                          value={detalle.tipoMatricula || ""}
                          onChange={(e) =>
                            handleChange(
                              index,
                              "tipoMatricula",
                              e.target.value
                            )
                          }
                          required
                          disabled={disabledAll}
                        >
                          <option value="">Seleccione…</option>
                          <option value="Ordinaria">Ordinaria</option>
                          <option value="Extraordinaria">Extraordinaria</option>
                        </select>
                      </div>

                      <div className="col-12 col-md-1 text-end">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeDetalle(index)}
                          disabled={disabledAll || !puedeEliminar}
                          title={
                            puedeEliminar
                              ? "Eliminar"
                              : "Sin permiso para eliminar"
                          }
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))
                )}

                <div className="pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        if (location.pathname === "/matricula/registrar") {
                          cleanData();
                        } else {
                          navigate("/matricula/listarmatriculas");
                        }
                      }}
                      disabled={loading}
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      form="form-matricula"
                      className="btn btn-dark"
                      disabled={disabledAll}
                    >
                      <i
                        className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                          } me-2`}
                      ></i>

                      {isEdit ? "Actualizar" : "Guardar"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarMatriculas;